import React from 'react';
import PropTypes from 'prop-types';
import Alert from 'react-s-alert';

import Users from 'api/resources/users';
import Bans from 'api/resources/bans';
import { processErrorByAlert } from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import IconBan from 'icons/Ban';

import PrimaryButton from 'styled/Buttons/PrimaryButton';
import SecondaryButton from 'styled/Buttons/SecondaryButton';
import Footer from 'components/ConfirmModal/Footer';
import Content from 'components/ConfirmModal/Content';
import Title from 'components/ConfirmModal/Title';
import ConfirmModal from 'components/ConfirmModal';
import ButtonLoader from 'components/Loader/ButtonLoader';
import Select from 'components/Select';
import Input from 'styled/Input';
import Label from 'styled/Label';

import BanButton from './BanButton';
import InputStub from './InputStub';

const BAN_DURATION_INTERVAL_LIST = [
  {
    label: 'На день',
    value: 'one_day',
  }, {
    label: 'На три дня',
    value: 'three_days',
  }, {
    label: 'На неделю',
    value: 'week',
  }, {
    label: 'Перманентный',
    value: 'permanent',
  },
];

export class UserBan extends React.PureComponent {
  state = {
    isOpen: false,
    reason: '',
    selectedDurationInterval: BAN_DURATION_INTERVAL_LIST[0].value,
    loading: false,
    currentBan: undefined,
  };

  get expiredAt() {
    return new Date(this.state.currentBan.expiredAt * 1000).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow',
    });
  }

  handleModalClose = () => {
    this.setState({ isOpen: false });
  }

  handleBanClick= () => {
    this.setState({ isOpen: true });

    if (this.props.isBanned) {
      this.loadUser();
    }
  }

  handleReasonChange = (e) => {
    this.setState({ reason: e.target.value });
  }

  handleDurationIntervalChange = (e) => {
    this.setState({ selectedDurationInterval: e.target.value });
  }

  handleBan = async () => {
    this.setState({ loading: true });

    try {
      const response = await Bans.ban({
        userId: this.props.userId,
        body: {
          data: {
            type: 'ban',
            attributes: {
              reason: this.state.reason,
              duration_interval: this.state.selectedDurationInterval,
            },
          },
        },
      });

      const ban = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      Alert.success(`Пользователь ${this.props.fullName} заблокирован`);
      this.setState({ loading: false, isOpen: false });

      this.props.onBan({ id: this.props.userId, currentBan: ban });
    } catch (err) {
      processErrorByAlert(err);
      this.setState({ loading: false });
    }
  }

  handleUnban = async () => {
    this.setState({ loading: true });

    try {
      await Bans.unban({ banId: this.state.currentBan.id });

      Alert.success(`Пользователь ${this.props.fullName} разблокирован`);
      this.setState({ loading: false, isOpen: false });

      this.props.onBan({ id: this.props.userId, currentBan: null });
    } catch (err) {
      processErrorByAlert(err);
      this.setState({ loading: false });
    }
  }

  loadUser = async () => {
    this.setState({ loading: true });

    try {
      const response = await Users.get({
        userId: this.props.userId,
        include: 'current_ban',
        fields: {
          user: 'current_ban',
          current_ban: 'is_permanent,reason,expired_at',
        },
      });

      const user = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState({
        currentBan: user.currentBan,
        loading: false,
      });
    } catch (err) {
      processErrorByAlert(err);
      this.setState({ loading: false });
    }
  }

  render() {
    return (
      <React.Fragment>
        <BanButton
          type="button"
          style={{ marginLeft: '12px' }}
          onClick={this.handleBanClick}
          ban={this.props.isBanned}
        >
          <IconBan />
        </BanButton>

        {!this.props.isBanned && (
          <ConfirmModal onClose={this.handleModalClose} isOpen={this.state.isOpen}>
            <Content>
              <Title style={{ marginBottom: '16px' }}>Заблокировать пользователя</Title>

              <Label htmlFor="durationInterval">Срок бана</Label>

              <Select
                style={{ marginBottom: '24px' }}
                id="durationInterval"
                onChange={this.handleDurationIntervalChange}
                value={this.state.selectedDurationInterval}
                disabled={this.state.loading}
              >
                {BAN_DURATION_INTERVAL_LIST.map(item => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </Select>

              <Label htmlFor="reason">Комментарий</Label>

              <Input
                id="reason"
                style={{ marginBottom: '14px' }}
                value={this.state.reason}
                onChange={this.handleReasonChange}
                placeholder="Напишите, за что блокируете?"
                disabled={this.state.loading}
              />
            </Content>

            <Footer>
              <PrimaryButton
                type="button"
                onClick={this.handleBan}
                autoFocus
                style={{ marginRight: '10px', minWidth: '154px' }}
                disabled={this.state.loading}
              >
                {this.state.loading ? <ButtonLoader /> : 'Заблокировать'}
              </PrimaryButton>

              <SecondaryButton
                type="button"
                onClick={this.handleModalClose}
                disabled={this.state.loading}
              >
                Отмена
              </SecondaryButton>
            </Footer>
          </ConfirmModal>
        )}

        {this.props.isBanned && (
          <ConfirmModal onClose={this.handleModalClose} isOpen={this.state.isOpen}>
            <Content>
              <Title style={{ marginBottom: '16px' }}>Разблокировать пользователя</Title>

              <Label htmlFor="expiredAt">Заблокирован до</Label>

              {this.state.currentBan
                ? (
                  <Input
                    id="expiredAt"
                    style={{ marginBottom: '14px' }}
                    value={this.state.currentBan.isPermanent ? 'Перманентный' : this.expiredAt}
                    disabled
                  />
                ) : (
                  <InputStub />
                )
              }

              <Label htmlFor="reason">Комментарий</Label>

              {this.state.currentBan
                ? (
                  <Input
                    id="reason"
                    style={{ marginBottom: '14px' }}
                    value={this.state.currentBan.reason}
                    placeholder="Причина не указана"
                    disabled
                  />
                ) : (
                  <InputStub />
                )
              }
            </Content>

            <Footer>
              <PrimaryButton
                type="button"
                onClick={this.handleUnban}
                autoFocus
                style={{ marginRight: '10px', minWidth: '156px' }}
                disabled={this.state.loading}
              >
                {this.state.loading ? <ButtonLoader /> : 'Разблокировать'}
              </PrimaryButton>

              <SecondaryButton
                type="button"
                onClick={this.handleModalClose}
              >
                Отмена
              </SecondaryButton>
            </Footer>
          </ConfirmModal>
        )}
      </React.Fragment>
    );
  }
}

UserBan.propTypes = {
  userId: PropTypes.string.isRequired,
  isBanned: PropTypes.bool,
  onBan: PropTypes.func,
  fullName: PropTypes.string,
};

UserBan.defaultProps = {
  isBanned: false,
  onBan: () => {},
  fullName: '',
};

export default UserBan;
